import type { ContentActionMenuItem } from "@setplex/arcana"

import type { ReactNode } from 'react'
import type { IContinueWatchingCardFormat } from '~/entities/continue-watching'
import type { EpisodeInfoPopupSource } from './lib'

import { ContentActionMenu, MenuAnchorSC } from "/app/libraries/arcana/src/blocks/ContentActionMenu/index.ts"
import { IconNames } from "/app/libraries/arcana/src/atoms/Icon/index.ts"

import { MediaTypes } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { useCallback, useMemo, useRef } from 'react'
import { t } from '~/core/i18n/i18next'
import { navigate } from '~/core/router'
import { formatTvShowProgramName } from '~/features/analytix/ui/lib'
import { contentAction } from '~/features/analytix/ui/models/content-actions'
import { useContentWatchedAction } from '~/features/content-watched/ui/useContentWatchedAction'
import { model as favoritesModel } from '~/features/favorites'
import { useRemoveFromContinueWatchingAction } from '~/features/remove-from-continue-watching/ui/useRemoveFromContinueWatchingAction'
import { model as guestModel } from '~/features/subscriber/guest'
import { model as infoPopupModel } from '~/widgets/Popups/InfoPopup'
import { createEpisodeInfoPopupPayload } from './lib'

type ContentWatchedMetadata = {
  watched?: boolean | null
}

type Props = {
  item: IContinueWatchingCardFormat
  locked?: boolean
  rowTitle?: string
  detailsHref: string
  children: ReactNode
}

export const ContinueWatchingActions = ({
  item,
  locked,
  rowTitle,
  detailsHref,
  children,
}: Props) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const { favorite, favorites, guest, openInfoPopup, trackContentAction } =
    useUnit({
      favorite: favoritesModel.favorite,
      favorites: favoritesModel.$favorites,
      guest: guestModel.$guest,
      openInfoPopup: infoPopupModel.openInfoPopup,
      trackContentAction: contentAction,
    })

  const episodeItem =
    item.contentType === MediaTypes.EPISODE
      ? (item as EpisodeInfoPopupSource)
      : null
  const tvShowId = episodeItem?.tvShowId
  const seasonId = episodeItem?.seasonId
  const isWithSeason = episodeItem?.isWithSeason
  const contentId = item.contentId
  const analyticsContentId =
    item.contentType === MediaTypes.EPISODE ? tvShowId : contentId
  const analyticsContentType =
    item.contentType === MediaTypes.EPISODE ? 'tvshow' : 'vod'
  const analyticsContentName =
    item.contentType === MediaTypes.EPISODE
      ? episodeItem?.tvShowName
      : item.contentName
  const analyticsProgramName =
    item.contentType === MediaTypes.EPISODE
      ? formatTvShowProgramName({
          seasonNumber: episodeItem?.seasonNumber,
          episodeNumber: episodeItem?.episodeNumber,
          episodeName: item.contentName,
        })
      : undefined

  const { menuItem: watchedMenuItem, handleWatchedSelect } =
    useContentWatchedAction({
      id: contentId,
      tvShowId,
      seasonId,
      isWithSeason,
      contentType: item.contentType,
      name: item.contentName,
      analyticsContentType,
      analyticsContentName,
      analyticsProgramName,
      analyticsRouteName: rowTitle,
      locked,
      blockedByAcl: item.blockedByAcl,
      hasPlaybackAccess: item.free === true || Boolean(item.purchaseInfo),
      watched: (item as ContentWatchedMetadata).watched,
    })

  const { menuItem: removeFromCwMenuItem, handleRemoveSelect } =
    useRemoveFromContinueWatchingAction({
      contentId,
      tvShowId,
      seasonId,
      isWithSeason,
      contentType: item.contentType,
      name: item.contentName,
      analyticsContentType,
      analyticsContentName,
      analyticsProgramName,
      analyticsRouteName: rowTitle,
      locked,
      blockedByAcl: item.blockedByAcl,
    })

  const handleDetailsSelect = useCallback(() => {
    trackContentAction({
      contentId: analyticsContentId,
      contentType: analyticsContentType,
      contentName: analyticsContentName,
      ...(analyticsProgramName ? { programName: analyticsProgramName } : {}),
      routeName: rowTitle,
      uiElementType: 'menu_option',
      uiElementName: 'details',
      actionName: 'tap',
    })

    if (item.contentType === MediaTypes.EPISODE) {
      openInfoPopup(
        createEpisodeInfoPopupPayload({
          contentId,
          contentName: item.contentName,
          episodeItem,
        })
      )
      return
    }

    navigate(detailsHref)
  }, [
    analyticsContentName,
    analyticsContentId,
    analyticsContentType,
    analyticsProgramName,
    contentId,
    detailsHref,
    episodeItem,
    item.contentName,
    item.contentType,
    openInfoPopup,
    rowTitle,
    trackContentAction,
  ])

  const handleMenuOpen = useCallback(() => {
    trackContentAction({
      contentId: analyticsContentId,
      contentType: analyticsContentType,
      contentName: analyticsContentName,
      ...(analyticsProgramName ? { programName: analyticsProgramName } : {}),
      routeName: rowTitle,
      uiElementType: 'button',
      uiElementName: 'kebab_button',
      actionName: 'tap',
    })
  }, [
    analyticsContentId,
    analyticsContentName,
    analyticsContentType,
    analyticsProgramName,
    rowTitle,
    trackContentAction,
  ])

  const handleMenuDismiss = useCallback(() => {
    trackContentAction({
      contentId: analyticsContentId,
      contentType: analyticsContentType,
      contentName: analyticsContentName,
      ...(analyticsProgramName ? { programName: analyticsProgramName } : {}),
      routeName: rowTitle,
      uiElementType: 'menu',
      uiElementName: 'content_context_menu',
      actionName: 'dismiss',
    })
  }, [
    analyticsContentId,
    analyticsContentName,
    analyticsContentType,
    analyticsProgramName,
    rowTitle,
    trackContentAction,
  ])

  const actionItems = useMemo<ContentActionMenuItem[]>(() => {
    if (!contentId || locked || item.blockedByAcl) return []

    const isVod = item.contentType === MediaTypes.VOD
    const isEpisode = item.contentType === MediaTypes.EPISODE

    const detailsAction: ContentActionMenuItem = {
      id: 'details',
      label: t('details'),
      icon: IconNames.info,
      onSelect: handleDetailsSelect,
    }

    const actionItemsResult: ContentActionMenuItem[] = []

    if ((isVod || isEpisode) && removeFromCwMenuItem) {
      actionItemsResult.push({
        ...removeFromCwMenuItem,
        onSelect: handleRemoveSelect,
      })
    }

    if ((isVod || isEpisode) && watchedMenuItem) {
      actionItemsResult.push({
        ...watchedMenuItem,
        onSelect: handleWatchedSelect,
      })
    }

    if (!guest && isVod) {
      const favoriteSelected =
        favorites.vod[contentId] ?? Boolean(item.favorite)

      actionItemsResult.push({
        id: 'favorite',
        label: t(favoriteSelected ? 'remove_from_my_list' : 'add_to_my_list'),
        icon: favoriteSelected ? IconNames.check : IconNames.plus,
        onSelect: () =>
          favorite({
            type: 'vod',
            id: contentId,
            favorite: favoriteSelected,
            name: item.contentName,
          }),
      })
    }

    actionItemsResult.push(detailsAction)

    return actionItemsResult
  }, [
    contentId,
    favorite,
    guest,
    favorites.vod,
    item.blockedByAcl,
    item.contentName,
    item.contentType,
    item.favorite,
    locked,
    handleDetailsSelect,
    handleRemoveSelect,
    handleWatchedSelect,
    removeFromCwMenuItem,
    watchedMenuItem,
  ])

  if (!actionItems.length) return <>{children}</>

  return (
    <MenuAnchorSC ref={anchorRef}>
      {children}
      <ContentActionMenu
        anchorRef={anchorRef}
        items={actionItems}
        ariaLabel={t('more')}
        onOpen={handleMenuOpen}
        onDismiss={handleMenuDismiss}
      />
    </MenuAnchorSC>
  )
}
